import { z } from 'zod';
import { coSellMsftApi } from '../../tackleApiClient';

const zBooleanStringEnumToBoolean = z
  .enum(['true', 'false'])
  .default('false')
  .transform((v) => v === 'true');

const AddressInput = z
  .object({
    addressLine1: z.string(),
    city: z.string(),
    postalCode: z.string(),
    country: z.string().optional(),
    region: z.string().optional(),
  })
  .strict()
  .transform((values) => ({
    ...values,
    // TODO: remove this when the API accepts country as a string
    country: 'US',
  }));

const MemberInput = z
  .object({
    firstName: z.string(),
    lastName: z.string(),
    phoneNumber: z.string(),
    email: z.string(),
  })
  .strict();

const CustomerProfileInput = z
  .object({
    name: z.string(),
    isMatchingComplete: zBooleanStringEnumToBoolean,
    isMaccEligible: zBooleanStringEnumToBoolean,
    ids: z.array(
      z
        .object({ id: z.string(), profileType: z.enum(['External', 'duns']) })
        .strict(),
    ),
    address: AddressInput,
    team: z.array(MemberInput),
  })
  .strict();

const BaseSolutionInput = z.object({
  id: z.string(),
  type: z.literal('Name').default('Name'),
  name: z.string(),
  publisherName: z.string(),
});

const ThirdPartySolutionInput = BaseSolutionInput.extend({
  solutionType: z.literal('3P'),
}).strict();

const MsftSolutionInput = BaseSolutionInput.extend({
  price: z.string(),
  quantity: z.number().int(),
  currency: z.string(),
  solutionType: z.literal('1P'),
}).strict();

export const SolutionInput = z.discriminatedUnion('solutionType', [
  ThirdPartySolutionInput,
  MsftSolutionInput,
]);

const RequirementsInput = z
  .object({
    solutionArea: z.string(),
    solutionPlay: z.string(),
    customerMarketplaceIntent: z
      .enum(['Yes', 'No', 'HaveNotDecided'])
      .default('Yes'),
    solutions: z.array(SolutionInput),
  })
  .strict();

const DetailsInput = z
  .object({
    notes: z.string().optional(),
    dealValue: z.number(),
    currency: z.string(),
    closingDateTime: z.string(),
    requirements: RequirementsInput,
  })
  .strict();

const InviteContextInput = z.object({
  organizationID: z.string(),
  organizationName: z.string(),
  notes: z.string(),
  assistanceRequestCode: z.enum([
    'GeneralOrOther',
    'CustomerTechnicalArchitecture',
    'ProofOfConceptOrDemo',
    'QuotesOrLicensing',
    'PostSalesCustomerSuccess',
    'WorkloadSpecificValueProposition',
  ]),
});

const DataMapper = z.object({
  '$tkl-metadata': z.string().optional(),
});

/** Schema to validate request body and transform to add fields that are not editable in the form.
 *
 */
export const CreateReferralInput = z
  .object({
    dataMapper: DataMapper.optional(),
    name: z.string(),
    crmId: z.string().optional(),
    crmOrgId: z.string().optional(),
    mpnId: z.string(),
    campaignId: z.string().optional(),
    dealSensitivity: z.enum(['USFed', 'None']).default('None'),
    customerProfile: CustomerProfileInput,
    details: DetailsInput,
    team: z.array(MemberInput),
    consent: z.object({
      consentToShareReferralWithMicrosoftSellers: z.boolean(),
    }),
    inviteContexts: z.array(InviteContextInput).optional(),
  })
  .strict()
  .transform((values) => ({
    ...values,
    /** required to create - when we need to mutate this value in the future,
     *  it can be set here using the values object
     */
    dealSensitivity: 'None',
    /** consent for the three default parameters is required to create
     *  consentToToShareInfoWithOthers is not a typo: https://learn.microsoft.com/en-us/partner-center/developer/referral-resources#consent
     */
    consent: {
      consentToShareReferralWithMicrosoftSellers:
        values.consent?.consentToShareReferralWithMicrosoftSellers,
      consentToToShareInfoWithOthers: true,
      consentToContact: true,
      consentToMicrosoftToContactSpecificPartners: true,
    },
  }));

const createIPReferral_Response = z.object({
  tackleId: z.string(),
  requestId: z.string(),
});

export const schemas = {
  AddressInput,
  MemberInput,
  CustomerProfileInput,
  SolutionInput,
  RequirementsInput,
  DetailsInput,
  CreateReferralInput,
  createIPReferral_Response,
};

const createIpReferral_TackleOperationId = z.enum([
  'createIPReferral',
  'createDraftIPReferral',
]);

/**
 *  Kicks off the creation of a new MSFT IP Referral in the co-sell-msft API.
 */
export async function createIPReferral({
  body,
  tackleOperationId,
}: {
  body: z.input<typeof CreateReferralInput>;
  tackleOperationId: z.infer<typeof createIpReferral_TackleOperationId>;
}) {
  const parsedBody = CreateReferralInput.parse(body);

  const response = await coSellMsftApi
    .post('api/referrals', {
      headers: {
        'tackle-operation-id':
          createIpReferral_TackleOperationId.parse(tackleOperationId),
      },
      body: JSON.stringify(parsedBody),
    })
    .json();

  const parsedResponse = createIPReferral_Response.parse(response);
  return parsedResponse;
}
